import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, AlertCircle, Loader2, ExternalLink, FileSpreadsheet, FolderOpen } from 'lucide-react';

interface DropboxFile {
  id: string;
  name: string;
  size?: number;
  modified?: string;
  path?: string;
}

export const DropboxSheetsIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [files, setFiles] = useState<DropboxFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingSheet, setIsCreatingSheet] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('dropbox_tokens')
        .select('*')
        .eq('user_id', user.id)
        .single();

      setIsConnected(!!data);
    } catch (error) {
      console.error('Error checking Dropbox connection:', error);
    }
  };

  const handleOAuthConnect = async () => {
    setIsConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call the enhanced OAuth integration for Dropbox
      const { data, error } = await supabase.functions.invoke('enhanced-oauth-integration', {
        body: { action: 'initialize_oauth', provider: 'dropbox' },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to initialize OAuth');
      }

      if (data?.authUrl) {
        // Redirect to OAuth authorization URL
        window.location.href = data.authUrl;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error: any) {
      console.error('Dropbox OAuth error:', error);
      toast.error(error.message || 'Failed to start OAuth flow');
      setIsConnecting(false);
    }
  };

  const loadFiles = async () => {
    if (!isConnected) return;

    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase.functions.invoke('enhanced-oauth-integration', {
        body: { 
          action: 'list_files',
          provider: 'dropbox',
          path: '/NextWaveUploads'
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      setFiles(data?.files || []);
      toast.success(`Loaded ${data?.files?.length || 0} files from Dropbox`);
    } catch (error: any) {
      console.error('Error loading files:', error);
      toast.error('Failed to load files from Dropbox');
    } finally {
      setIsLoading(false);
    }
  };

  const createGoogleSheet = async () => {
    setIsCreatingSheet(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      toast.info('Creating Google Sheet in Dropbox...');

      // Create a CSV file and upload to Dropbox
      const { data, error } = await supabase.functions.invoke('enhanced-oauth-integration', {
        body: { 
          action: 'create_spreadsheet',
          provider: 'dropbox',
          fileName: `NextWave_Analytics_${new Date().toISOString().split('T')[0]}.csv`,
          data: [
            ['Date', 'Metric', 'Value', 'Category'],
            [new Date().toISOString().split('T')[0], 'Revenue', '10000', 'Sales'],
            [new Date().toISOString().split('T')[0], 'Leads', '250', 'Marketing'],
            [new Date().toISOString().split('T')[0], 'Conversion Rate', '2.5%', 'Analytics']
          ]
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success('Google Sheet created and uploaded to Dropbox successfully!');
      await loadFiles(); // Refresh file list
    } catch (error: any) {
      console.error('Error creating Google Sheet:', error);
      toast.error('Failed to create Google Sheet in Dropbox');
    } finally {
      setIsCreatingSheet(false);
    }
  };

  const runAIWorkflow = async () => {
    try {
      toast.info('Starting AI workflow with Dropbox + Google Sheets data...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase.functions.invoke('enhanced-data-refresh', {
        body: { 
          source: 'dropbox_sheets',
          includeAIWorkflow: true
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success('AI workflow with Dropbox + Google Sheets started successfully!');
    } catch (error: any) {
      console.error('Error running AI workflow:', error);
      toast.error('Failed to run AI workflow');
    }
  };

  const disconnect = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('dropbox_tokens')
        .delete()
        .eq('user_id', user.id);

      setIsConnected(false);
      setFiles([]);
      toast.success('Disconnected from Dropbox');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect from Dropbox');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
              <FolderOpen className="w-4 h-4 text-white" />
            </div>
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
            Dropbox + Google Sheets
            {isConnected && (
              <Badge variant="default" className="bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Connected
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Connect Dropbox to automatically create and manage Google Sheets for analytics. The AI workflow will process data from spreadsheets stored in your Dropbox.
            </AlertDescription>
          </Alert>

          {!isConnected ? (
            <Button 
              onClick={handleOAuthConnect} 
              disabled={isConnecting}
              className="w-full"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting to Dropbox...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect with Dropbox OAuth
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                <Button onClick={loadFiles} disabled={isLoading} variant="outline">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load Files'
                  )}
                </Button>
                <Button 
                  onClick={createGoogleSheet} 
                  disabled={isCreatingSheet} 
                  variant="outline"
                  className="bg-green-50 hover:bg-green-100"
                >
                  {isCreatingSheet ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Create Sheet
                    </>
                  )}
                </Button>
                <Button onClick={runAIWorkflow} variant="outline">
                  Run AI Workflow
                </Button>
                <Button onClick={disconnect} variant="outline">
                  Disconnect
                </Button>
              </div>

              {files.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Files in Dropbox</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {files.slice(0, 5).map((file, index) => (
                      <div key={file.id || index} className="p-3 border rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-green-600" />
                          <div className="font-medium">{file.name}</div>
                        </div>
                        {file.size && (
                          <div className="text-sm text-muted-foreground">
                            Size: {(file.size / 1024).toFixed(1)} KB
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {files.length === 0 && !isLoading && (
                <div className="text-center py-6">
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">
                    No files found. Create a Google Sheet to get started.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};